import { computed, ref } from 'vue'

import type { SavedAddress } from '@/composables/useAddresses'
import { ORDER_TYPE, type OrderType } from '@/composables/useDelivery'
import type { LngLat } from '@/composables/useLocation'
import { useCartStore } from '@/stores/cart'
import { useLocationStore } from '@/stores/location'
import { useMenuStore } from '@/stores/menu'
import { FLOW, track } from '@/utils/track'

export interface LocationChoice {
  delivery: OrderType
  /** Точка доставки; у самовывоза её нет */
  coords?: LngLat
  /** Выбранный филиал; у доставки его определяет бэкенд по координатам */
  restaurantId?: string
  address?: string
  /** Запись справочника, если клиент выбрал готовый адрес, а не точку на карте */
  addressId?: number
  /** Номер дома из разбора геокодера — форма адреса просит его подтвердить */
  house?: string
  /** Регион сохранённого адреса — он может быть из другого города */
  region?: number
}

export interface LocationResult {
  ok: boolean
  /** Точку видим впервые: ведём на форму, там её и сохраним */
  isNewAddress?: boolean
}

/**
 * Подтверждение точки (§4 LOGIC.md). Порядок важен: сначала снимок текущего
 * состояния, потом запись нового и перезапрос меню. Меню — единственная
 * проверка, что адрес в зоне доставки: если оно ответило ошибкой, состояние
 * откатывается, и клиент остаётся на карте.
 */
export function useLocationConfirm() {
  const locationStore = useLocationStore()
  const menuStore = useMenuStore()
  const cartStore = useCartStore()

  const isSaving = ref(false)

  /** Точка подтверждена на карте, но ещё не сохранена формой «Новый адрес». */
  const pendingAddress = ref<{ coords: LngLat; name: string; house: string } | null>(null)

  /**
   * Запись справочника, в которую попала точка. Клиент поставил пин там, где
   * у него уже есть адрес: новую запись плодить нельзя, а детали он сейчас
   * вводит — значит форма дописывает их в эту.
   */
  const pendingSaved = ref<SavedAddress | null>(null)

  /** Чем заполнить форму деталей: у попавшей записи они уже есть. */
  const pendingInitial = computed<Record<string, string>>(() => ({
    label: pendingSaved.value?.label ?? '',
    house: pendingAddress.value?.house || (pendingSaved.value?.house ?? ''),
    entrance: pendingSaved.value?.entrance ?? '',
    floor: pendingSaved.value?.floor ?? '',
    flat: pendingSaved.value?.flat ?? '',
    comment: pendingSaved.value?.comment ?? '',
  }))

  async function confirmLocation(choice: LocationChoice): Promise<LocationResult> {
    const previous = locationStore.snapshot()
    pendingSaved.value = null

    /*
     * Пара взаимоисключающая: при доставке ведут координаты, а филиал
     * подставит бэкенд; при самовывозе ведёт филиал, координаты обнуляются,
     * иначе бэкенд посчитает доставку по чужой точке.
     */
    locationStore.setActiveDelivery(choice.delivery)
    locationStore.setRestaurantId(choice.restaurantId ?? '')
    /* У самовывоза филиал выбирает клиент — регион подтягиваем за ним */
    if (choice.restaurantId) locationStore.syncRegionByRestaurant(choice.restaurantId)
    locationStore.setLongLat(choice.coords ?? [0, 0])
    /* У самовывоза адреса нет — шапка там показывает филиал */
    locationStore.setAddressName(choice.delivery === ORDER_TYPE.delivery ? (choice.address ?? '') : '')
    /*
     * Точку с карты справочник ещё не знает — id появится, когда её сохранит
     * форма. Старый сбрасываем обязательно: иначе заказ уехал бы с подъездом
     * и этажом от прошлого адреса.
     */
    locationStore.setAddressId(choice.addressId ?? 0)
    if (choice.region !== undefined) locationStore.setRegionId(choice.region)

    // В другом ресторане свои цены и ассортимент — старая корзина не годится
    cartStore.resetCart()

    isSaving.value = true
    await menuStore.getMenu()
    isSaving.value = false

    const type = choice.delivery === ORDER_TYPE.delivery ? 'delivery' : 'pickup'

    if (menuStore.menuError) {
      /* Главный обрыв воронки: клиент выбрал точку, а везти туда некому */
      track(FLOW.pointRejected, { type, reason: menuStore.menuError })
      locationStore.restore(previous)
      return { ok: false }
    }

    track(FLOW.pointConfirmed, { type })

    if (choice.delivery !== ORDER_TYPE.delivery || !choice.coords) return { ok: true }

    const [longitude, latitude] = choice.coords

    /*
     * Тот же дом клиент дважды в ту же точку не поставит, поэтому ищем
     * ближайший сохранённый. Нашли — выбираем именно его координаты: иначе
     * адрес будет уползать на десяток метров при каждом выборе и однажды
     * выйдет за радиус, превратившись в дубль.
     */
    const saved = locationStore.findNearest(longitude, latitude)
    if (saved) {
      locationStore.setLongLat([saved.longitude, saved.latitude])
      locationStore.setAddressName(saved.name)
      locationStore.setAddressId(saved.id)

      /* Форму показывать не обязательно, но если её откроют — правим эту запись */
      pendingSaved.value = saved
      pendingAddress.value = {
        coords: [saved.longitude, saved.latitude],
        name: saved.name,
        house: saved.house ?? '',
      }

      return { ok: true, isNewAddress: false }
    }

    pendingAddress.value = {
      coords: choice.coords,
      name: choice.address ?? '',
      house: choice.house ?? '',
    }
    return { ok: true, isNewAddress: true }
  }

  /** Точка подтверждена, но форму мы не открываем — правим существующий адрес. */
  function dropPending(): void {
    pendingAddress.value = null
    pendingSaved.value = null
  }

  /**
   * В справочник адрес попадает только с формы: с карты у него ещё нет
   * деталей. Сохраняет бэкенд — не получилось, оставляем форму открытой,
   * об ошибке клиенту расскажет тост запроса.
   */
  async function saveAddress(details: Record<string, string>): Promise<boolean> {
    const pending = pendingAddress.value
    if (!pending) return false

    /*
     * Точка попала в уже существующий адрес — дописываем детали в него.
     * Координаты не трогаем: запись и так стоит там, куда мы увели точку,
     * а вторая запись в том же доме только засорила бы справочник и CRM.
     */
    const target = pendingSaved.value
    if (target) {
      const ok = await locationStore.updateAddress(target.id, details)
      if (!ok) return false

      locationStore.setAddressId(target.id)
      locationStore.setAddressName(target.name)
      track(FLOW.addressSaved, { edited: true })
      dropPending()
      return true
    }

    const saved = await locationStore.addAddress({
      ...details,
      name: pending.name,
      /* Дом из разбора адреса — в форме его правят как «Кв./Офис», в заказ он идёт строкой */
      ...(pending.house ? { house: pending.house } : {}),
      longitude: pending.coords[0],
      latitude: pending.coords[1],
      region: locationStore.currentRegionId,
    })
    if (!saved) return false

    /*
     * Едем именно по сохранённой записи: `addAddress` мог не создать новую, а
     * вернуть соседнюю из справочника — тогда и точка должна стать её.
     */
    locationStore.setAddressId(saved.id)
    locationStore.setLongLat([saved.longitude, saved.latitude])
    locationStore.setAddressName(saved.name)

    track(FLOW.addressSaved, { house: Boolean(pending.house) })
    pendingAddress.value = null
    return true
  }

  return {
    isSaving,
    pendingAddress,
    pendingSaved,
    pendingInitial,
    confirmLocation,
    saveAddress,
    dropPending,
  }
}
